// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useMemo } from 'react';
import { API_ACCESS_TOKENS, API_MANUFACTURERS } from 'Constants';
import useAnalytics from 'Hooks/useAnalytics';
import useUser from 'Hooks/useUser';
import useFetch from 'Hooks/useFetch';
import useCategories from 'Hooks/useCategories';
import useAsyncError from 'Hooks/useAsyncError';
import URLFeedGeneratorComponent from 'Components/APIIntegrations/URLFeedGenerator';

const defaultParameters = {
  // basic parameters
  category_id: '',
  manufacturer_id: '',
  currency_id: 'ron',
  format: 'csv',
  language: 'ro',

  // boolean toggles
  with_vat: false,
  resealed: false,
  with_inactive: false,

  // columns array
  columns: {
    code: false,
    ean: false,
    title: false,
    description: false,
    full_description: false,
    manufacturer_id: false,
    manufacturer_name: false,
    category_level_1_id: false,
    category_level_1_name: false,
    category_level_2_id: false,
    category_level_2_name: false,
    category_level_3_id: false,
    category_level_3_name: false,
    stock: false,
    reserved_stock: false,
    supplier_stock: false,
    supplier_stock_delivery_date: false,
    minimum_quantity: false,
    price: false,
    promo_price: false,
    is_special_price: false,
    special_price_valid_to: false,
    green_stamp: false,
    is_new: false,
    is_eol: false,
    is_on_demand: false,
    length: false,
    width: false,
    height: false,
    length_unit_of_measure: false,
    weight: false,
    weight_unit_of_measure: false,
    warranty: false,
    delivery_time: false,
    on_demand_delivery_time: false,
    pictures: false,
    attributes: false,
    filters: false,
    has_resealed: false,
    original_product_id: false,
    created_at: false,
    updated_at: false,
    taric_code: false,
    version: false,
  }
};

const fileFormatOptions = [
  { id: 'csv', label: 'CSV' },
  { id: 'json', label: 'JSON' },
  { id: 'xml', label: 'XML' },
];

const currencyOptions = [
  { id: 'ron', label: 'RON' },
  { id: 'eur', label: 'EUR' },
  { id: 'usd', label: 'USD' },
];

const languageOptions = [
  { id: 'ro', translationKey: 'romanian' },
  { id: 'hu', translationKey: 'hungarian' },
  { id: 'en', translationKey: 'english' },
];

const URLFeedGenerator = () => {
  const { data: { email, loggedIn } = {} } = useUser();
  const fetcher = useFetch();
  const { data: categories } = useCategories();
  const analytics = useAnalytics();
  const throwError = useAsyncError();
  const [baseUrl, setBaseUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [parameters, setParameters] = useState({ ...defaultParameters });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  useEffect(() => {
    if (loggedIn) {
      getAPIInformation();
    }
  }, [email, loggedIn]);

  useEffect(() => {
    if (selectedCategories.length > 0) {
      const ids = selectedCategories.map(cat => encodeURIComponent(cat.value)).join(',');
      setParameters(prev => ({
        ...prev,
        category_id: ids
      }));
    } else {
      setParameters(prev => ({
        ...prev,
        category_id: ''
      }));
    }

    if (selectedBrands.length > 0) {
      const brandIds = selectedBrands.map(brand => encodeURIComponent(brand.value)).join(',');
      setParameters(prev => ({
        ...prev,
        manufacturer_id: brandIds
      }));
    } else {
      setParameters(prev => ({
        ...prev,
        manufacturer_id: ''
      }));
    }
  }, [selectedCategories, selectedBrands]);

  useEffect(() => {
    const fetchFilteredManufacturers = async () => {
      try {
        const categoryIds = selectedCategories.map(cat => cat.value);
        const manufacturersRes = await fetcher(`${API_MANUFACTURERS}?product_category_id[]=${categoryIds.join('&product_category_id[]=')}`);
        const newBrands = (manufacturersRes?.manufacturers || []).map(brand => ({
          value: brand.id,
          label: brand.name
        }));

        setBrands(newBrands);

        if (selectedBrands.length > 0) {
          const availableBrandIds = new Set(newBrands.map(brand => brand.value));
          const filteredSelectedBrands = selectedBrands.filter(brand => availableBrandIds.has(brand.value));
          setSelectedBrands(filteredSelectedBrands);
        }
      } catch (err) {
        throwError(err);
      }
    };

    if (selectedCategories.length > 0) {
      fetchFilteredManufacturers();
    } else {
      getAPIInformation();
    }
  }, [selectedCategories]);

  const flattenCategories = useMemo(() => {
    const flattenOptions = (cats, level = 0, parentPath = []) => {
      const result = [];

      cats.forEach(cat => {
        const currentPath = [...parentPath, cat.name];
        const hasChildren = cat.children && cat.children.length > 0;

        result.push({
          value: cat.id,
          label: cat.name,
          level,
          fullPath: currentPath.join(' > '),
          category: cat,
          parentPath,
          hasChildren,
          parentId: cat.parent_id || null
        });

        if (hasChildren) {
          result.push(...flattenOptions(cat.children, level + 1, currentPath));
        }
      });

      return result;
    };

    return flattenOptions(categories);
  }, [categories]);

  const getDescendantIds = (cat, ids = new Set()) => {
    if (cat.children && cat.children.length > 0) {
      cat.children.forEach(child => {
        ids.add(child.id);
        getDescendantIds(child, ids);
      });
    }
    return ids;
  };

  const visibleCategories = useMemo(() => {
    if (!selectedCategories.length) return flattenCategories;

    const selectedIds = new Set(selectedCategories.map(c => c.value || c));

    const descendantIds = new Set();
    flattenCategories.forEach(cat => {
      if (selectedIds.has(cat.value) && cat.category.children && cat.category.children.length > 0) {
        getDescendantIds(cat.category, descendantIds);
      }
    });

    return flattenCategories.filter(cat => !descendantIds.has(cat.value));
  }, [flattenCategories, selectedCategories]);

  const handleCategoryChange = selected => {
    if (!selected || selected.length === 0) {
      setSelectedCategories([]);
      return;
    }

    const lastSelected = selected[selected.length - 1];

    const descendantIds = new Set();
    if (lastSelected.category?.children) {
      getDescendantIds(lastSelected.category, descendantIds);
    }

    const filtered = selected.filter(cat => {
      const catId = cat.value || cat;
      return !descendantIds.has(catId);
    });

    setSelectedCategories(filtered);
  };

  const handleBrandChange = selected => {
    setSelectedBrands(selected || []);
  };

  const getAPIInformation = async () => {
    setLoading(true);

    try {
      const [tokensRes, manufacturersRes] = await Promise.all([
        fetcher(API_ACCESS_TOKENS),
        fetcher(API_MANUFACTURERS)
      ]);

      const token = tokensRes?.[0];
      if (token && new Date(token.expiresAt) > new Date()) {
        setBaseUrl(`${window.location.origin}/products/${token.token}/feed/`);
        setApiKey(token.token);
      }

      const brandOptions = (manufacturersRes?.manufacturers || []).map(({ id, name }) => ({
        value: id,
        label: name
      }));
      setBrands(brandOptions);
    } catch (err) {
      throwError(err);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = () => {
    setLoading(true);

    fetcher(API_ACCESS_TOKENS, {
      method: 'POST',
      body: JSON.stringify({})
    })
      .then(res => {
        analytics.track('generate_api_key');

        const now = new Date();

        const isValid = res && new Date(res.expiresAt) > now;

        if (isValid) {
          setBaseUrl(`${window.location.origin}/products/${res.token}/feed/`);
          setApiKey(res.token);
        }
      })
      .catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  const handleParameterChange = (key, value) => {
    analytics.track('parameter_change', { key, value });
    const defaults = {
      format: 'csv',
      currency_id: 'ron',
      language: 'ro'
    };

    setParameters(prev => ({
      ...prev,
      [key]: value || defaults[key] || value
    }));
  };

  const handleColumnChange = (column, checked) => {
    analytics.track('column_change', { column, checked });
    setParameters(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [column]: checked
      }
    }));
  };

  const clearAllParameters = () => {
    analytics.track('clear_all_parameters');
    setParameters({ ...defaultParameters });
    setSelectedCategories([]);
    setSelectedBrands([]);
  };

  const clearParameter = param => {
    analytics.track('clear_parameter', { ...param });
    setParameters(prev => {
      const { key, value } = param;

      const defaults = {
        format: 'csv',
        currency_id: 'ron',
        language: 'ro'
      };

      if (key === 'column') {
        return {
          ...prev,
          columns: {
            ...prev.columns,
            [value]: false
          }
        };
      }

      return {
        ...prev,
        [key]: defaults[key] ?? ''
      };
    });
  };

  const generateUrl = () => {
    const params = [];

    // add currency and format parameters
    if (parameters.currency_id) params.push(`currency_id=${encodeURIComponent(parameters.currency_id)}`);
    if (parameters.format) params.push(`format=${encodeURIComponent(parameters.format)}`);

    // add boolean parameters
    if (parameters.with_vat) params.push('with_vat=true');
    if (parameters.resealed) params.push('resealed=true');
    if (parameters.with_inactive) params.push('with_inactive=true');

    // add selected columns without encoding the brackets
    Object.keys(parameters.columns).forEach(column => {
      if (parameters.columns[column]) {
        params.push(`columns[]=${encodeURIComponent(column)}`);
      }
    });

    // add manufacturers array format
    if (selectedBrands.length > 0) {
      selectedBrands.forEach(brand => {
        params.push(`manufacturers[]=${encodeURIComponent(brand.value)}`);
      });
    }

    // add categories array format
    if (selectedCategories.length > 0) {
      selectedCategories.forEach(cat => {
        params.push(`categories[]=${encodeURIComponent(cat.value)}`);
      });
    }

    if (parameters.language !== 'ro') params.push(`language=${encodeURIComponent(parameters.language)}`);

    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
  };

  return (
    <URLFeedGeneratorComponent
      parameters={ parameters }
      fileFormatOptions={ fileFormatOptions }
      currencyOptions={ currencyOptions }
      languageOptions={ languageOptions }
      onParameterChange={ handleParameterChange }
      onColumnChange={ handleColumnChange }
      onClearAll={ clearAllParameters }
      onClearParameter={ clearParameter }
      generatedUrl={ generateUrl() }
      loading={ loading }
      generateApiKey={ generateApiKey }
      apiKey={ apiKey }
      handleCategoryChange={ handleCategoryChange }
      selectedCategories={ selectedCategories }
      categories={ visibleCategories }
      handleBrandChange={ handleBrandChange }
      selectedBrands={ selectedBrands }
      brands={ brands }
    />
  );
};

URLFeedGenerator.displayName = 'URLFeedGeneratorContainer';

export default URLFeedGenerator;
